import { createContext, memo, useEffect, useRef, useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import COMMON from "common";
import api from "services/api";
import services from "services";
import pathnames from "routes/pathnames";
import sanitizeError from "common/sanitize-error";
import useActiveState from "hooks/use-active-state";
import initialiseAmitabha from "common/initialise-amitabha";
import serveRequestErrors from "common/serve-request-errors";

const sharedStyles = "margin: 5px 0; padding: 8px; border-radius: 20px; color: #FFFFFF;";
const initStyles = `${sharedStyles} background: #673AB7;`;
const ejectStyles = `${sharedStyles} background: #ffb127;`;
const reqDataStyles = `${sharedStyles} background: #F8EA8C;`;
const reqParamsStyles = `${sharedStyles} background: #ff9514;`;
const respSuccessStyles = `${sharedStyles} background: #32ac97;`;
const errorStyles = `${sharedStyles} background: #F79489;`;
const debug = process.env.NODE_ENV === "development";

const interceptorDebug = (title, styles, data = null) => {
	const isApp = sessionStorage.getItem(COMMON.EVENTS.APP);
	if (!debug) return;

	console.log(`%c ${title}`, styles);

	if (data && isApp) {
		const rnWebViewPostMessage = window?.postMessage;

		if (rnWebViewPostMessage) {
			rnWebViewPostMessage(title);
			rnWebViewPostMessage(JSON.stringify(data));
		}
	} else if (data) {
		console.log(data);
	}

	console.log("");
};

export const AxiosContext = createContext();

const InterceptorProvider = ({ children, routes }) => {
	const controller = useRef({});
	const dispatch = useDispatch();
	const [initialed, setInitialed] = useState(false);
	const requestInterceptor = useRef(null);
	const responseInterceptor = useRef(null);
	const isActiveState = useActiveState();

	//prettier-ignore
	const onHandleForceLogout = useCallback(async (error, path = pathnames.login) => {
		localStorage.clear();
		sessionStorage.clear();
		routes.navigate(path);
		dispatch({ type: COMMON.REDUX_ACTION.LOGOUT });
		serveRequestErrors(error);
	}, [routes, dispatch]);

	//prettier-ignore
	const onHandleVerifyMaintenance = useCallback(async (error) => {
		let response = null;

		try {
			response = await api.get.general.maintenance();
		} catch (catchError) {
			serveRequestErrors(catchError);
		}

		const maintenance = response?.maintenance?.enabled;

		const isMaintenancePage = window.location.pathname === pathnames.maintenance;

		if (maintenance) {
			routes.navigate(pathnames.maintenance);
		} else if (isMaintenancePage) {
			routes.navigate(pathnames.login);
		}

		if (error) return Promise.reject(error);
	}, [routes]);

	const onHandleRequest = useCallback((config) => {
		switch (config.method) {
			case "get":
				interceptorDebug(`GET REQUESTING 🚀 ${config.url}`, reqParamsStyles, config.params);
				break;
			default:
				interceptorDebug(`POST REQUESTING 🚀 ${config.url}`, reqDataStyles, config.data);
				break;
		}

		let token = null;
		let signal = {};

		if (config.cancelId) {
			controller.current[config.cancelId] = new AbortController();
			signal = { signal: controller.current[config.cancelId].signal };
		}

		const localToken = localStorage.getItem(COMMON.AUTH_TOKEN);
		const sessionToken = sessionStorage.getItem(COMMON.AUTH_TOKEN);

		if (localToken) {
			token = localToken;
		} else {
			token = sessionToken;
		}

		if (token && config.token !== false) config.headers.Authorization = "Bearer " + token;

		return { ...config, ...signal };
	}, []);

	const onHandleRequestError = useCallback((error) => {
		interceptorDebug("REQUESTING ERROR 👎", errorStyles, sanitizeError(error));
		return Promise.reject(error);
	}, []);

	const onHandleResponse = useCallback((response) => {
		interceptorDebug(`RESPONSE SUCCESS: 🌟 ${response.config.baseURL + response.config.url}`, respSuccessStyles, response.data);
		return response.data;
	}, []);

	//prettier-ignore
	const onResponseError = useCallback(async (error) => {
		interceptorDebug(`RESPONSE ERROR 🥲 ${error.config.baseURL + error.config.url}`, errorStyles, error);
		const isApp = sessionStorage.getItem(COMMON.EVENTS.APP);
		const errorResponse = error?.response;
		const code = errorResponse?.status;
		const errorCode = errorResponse?.data?.errorCode;
		const unauthorized = code === 403;
		const maintenance = code === 502;

		switch (errorCode) {
			case COMMON.STATUS_CODE.TOKEN_EXPIRED:
				try {
					const stayLogged = localStorage.getItem(COMMON.AUTH_TOKEN);
					const refreshToken = stayLogged ? localStorage.getItem(COMMON.REFRESH_TOKEN) : sessionStorage.getItem(COMMON.REFRESH_TOKEN);
					const response = await api.post.general.refreshToken({ refreshToken });

					if (stayLogged) {
						localStorage.setItem(COMMON.AUTH_TOKEN, response.accessToken);
						localStorage.setItem(COMMON.REFRESH_TOKEN, response.refreshToken);
					} else {
						sessionStorage.setItem(COMMON.AUTH_TOKEN, response.accessToken);
						sessionStorage.setItem(COMMON.REFRESH_TOKEN, response.refreshToken);
					}

					error.config.headers.Authorization = "Bearer " + response.accessToken;

					return services.request(error.config);
				} catch (catchError) {
					if(isApp) console.log(COMMON.EVENTS.LOGOUT);
					else onHandleForceLogout(error);
				}

				return Promise.reject(error);
			case COMMON.STATUS_CODE.USER_BLOCKED:
				if(isApp) console.log(COMMON.EVENTS.LOGOUT);
				else onHandleForceLogout(error, pathnames.accountSuspended);
				return Promise.reject(error);
			case COMMON.STATUS_CODE.INVALID_TOKEN:
				return Promise.reject(error);
			default:
				if(unauthorized) {
					if(isApp) console.log(COMMON.EVENTS.LOGOUT);
					else onHandleForceLogout(error, pathnames.accountSuspended);

					return Promise.reject(error);
				} else if(maintenance) {
					return onHandleVerifyMaintenance(error);
				} else {
					return Promise.reject(error);
				}
		}
	}, [onHandleForceLogout, onHandleVerifyMaintenance]);

	const onHandleCancelRequest = useCallback((id) => {
		if (controller.current[id]) controller.current[id]?.abort();
	}, []);

	const onHandleEjectAxiosInterceptor = useCallback(() => {
		if (requestInterceptor.current !== null) {
			interceptorDebug("Ejected Request Axios Interceptor! 👋", ejectStyles);
			services.interceptors.request.eject(requestInterceptor.current);
		}
		if (responseInterceptor.current !== null) {
			interceptorDebug("Ejected Response Axios Interceptor! 👋", ejectStyles);
			services.interceptors.response.eject(responseInterceptor.current);
		}
	}, []);

	useEffect(() => {
		initialiseAmitabha();
		interceptorDebug(`Init Axios Interceptor! 🎉`, initStyles);
		requestInterceptor.current = services.interceptors.request.use(onHandleRequest, onHandleRequestError, { synchronous: true });
		responseInterceptor.current = services.interceptors.response.use(onHandleResponse, onResponseError, { synchronous: true });
		setInitialed(true);
	}, [onHandleRequest, onHandleRequestError, onHandleResponse, onResponseError]);

	useEffect(() => {
		if (isActiveState) onHandleVerifyMaintenance();

		return () => {
			onHandleCancelRequest(COMMON.ENDPOINT_PATH.GENERAL.MAINTENANCE);
		};
	}, [onHandleVerifyMaintenance, onHandleCancelRequest, isActiveState, routes]);

	useEffect(() => {
		return () => {
			onHandleEjectAxiosInterceptor();
		};
	}, [onHandleEjectAxiosInterceptor]);

	if (!initialed) return null;

	return <AxiosContext.Provider value={{ onHandleCancelRequest }}>{children}</AxiosContext.Provider>;
};

export default memo(InterceptorProvider);
